import cronsTrue from "cronstrue";
import { addHours, isAfter, isBefore } from "date-fns/esm";
import prettyBytes from "pretty-bytes";
import satisfies from "semver/functions/satisfies";
import maxSatisfying from "semver/ranges/max-satisfying";
import validRange from "semver/ranges/valid";
import * as z from "zod";

import { extractRepositoryDetails } from "components/Forms/SourceCode/RepositoryUrlField/utils";
import { TerraformWorkflowTool as WorkflowTool } from "types/generated";

const validateWorkspaceField = (value: string): string => {
  if (value && value.length > 90) {
    return "Workspace names need to be 90 characters or less";
  }

  if (value && !value.match(/^[a-z0-9-_]+$/i)) {
    return "Workspace names can only include letters, numbers, - and _";
  }

  return "";
};

const validateVersionRange = (
  range?: string | null,
  supportedVersions: string[] = [],
  vendor = "Terraform"
): string => {
  const stringIsRequiredValidation = stringIsRequired("Please select a version range.")(range);
  if (stringIsRequiredValidation !== true) {
    return stringIsRequiredValidation;
  }

  const validatedRange = validRange(range);

  if (validatedRange === null) {
    return "Please enter a valid version range.";
  }

  if (!supportedVersions.length) {
    return "";
  }

  for (const version of supportedVersions) {
    if (satisfies(version, range ?? "")) {
      return "";
    }
  }

  return `No supported ${vendor} version (${supportedVersions[supportedVersions.length - 1]} - ${
    supportedVersions[0]
  }) satisfies constraints ${range}`;
};

const validateUseSmartSanitizationField = (
  useSmartSanitization: boolean,
  terraformVersion?: string | null,
  versionsList?: string[],
  workflowTool = WorkflowTool.TerraformFoss
): string => {
  const stringIsRequiredValidation = stringIsRequired("Please select a version range.")(
    terraformVersion
  );
  if (stringIsRequiredValidation !== true) {
    return stringIsRequiredValidation;
  }

  if (!useSmartSanitization) {
    return "";
  }

  // Each OpenTofu version should support smart sanitization
  if (workflowTool == WorkflowTool.OpenTofu) {
    return "";
  }

  // Skip validation for custom workflow tool (we expect customer will know what they are doing)
  // TODO: add warning in the frontend when Custom workflow tool + smart sanitization is selected
  if (workflowTool == WorkflowTool.Custom) {
    return "";
  }

  // we looking for the max satisfying version in the range of versions (same as in the backend)
  let maxSatisfyingTFVersion;
  try {
    // smart sanitization is only supported on terraform version 1.0.1 and above
    // to check this, we need to parse the version string/range, then determine that the range
    // is a subset of the range `>=1.0.1
    maxSatisfyingTFVersion = maxSatisfying(versionsList ?? [], terraformVersion ?? "");
  } catch {
    //ignore the error
  }

  // Terraform version is validated by the validateVersionRange function
  // so we should ignore the error here
  if (!maxSatisfyingTFVersion) {
    return "";
  }

  if (!satisfies(maxSatisfyingTFVersion, ">=1.0.1", { includePrerelease: true })) {
    return "Smart sanitization is only supported on Terraform version 1.0.1 and above";
  }

  return "";
};

export const validateEmail = (email: string) => {
  const isValid =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
      email
    );

  if (!isValid) {
    return "Email is not valid";
  }

  return undefined;
};

export const validatePhoneNumber = (phoneNumber: string) => {
  const isValid = /^[+]?[1-9][\d\s\-()]{7,15}$/.test(phoneNumber?.trim());

  if (!isValid) {
    return "Phone number is not valid";
  }

  return undefined;
};

export const stackValidator = {
  validateWorkspaceField,
  validateUseSmartSanitizationField,
  validateVersionRange,
};

export const stringIsRequired =
  (message = "This field is required.") =>
  (value?: string | null) =>
    z.string().trim().min(1).safeParse(value).success || message;

export const validateFileSize = (file: File, maxSize: number) => {
  if (file && file.size > maxSize) {
    return `File is too large, max size is ${prettyBytes(maxSize)}`;
  }

  return true;
};

export const validateUnixVariableName = (existingNames?: string[]) => (value: string) => {
  // https://github.com/spacelift-io/backend/blob/17556fce4b3417c60b9f778c96d8f3019315fc90/shared/config_element.go#L25
  const PATTERN = /^[a-zA-Z_]+[a-zA-Z0-9_]*$/;

  if (!PATTERN.test(value)) {
    return "Name must be a valid UNIX environment variable name.";
  }

  if (existingNames && existingNames.length > 0) {
    const uniqueValidation = validateUniqueValue(
      existingNames,
      "A variable with this name already exists."
    )(value);

    if (uniqueValidation !== true) {
      return uniqueValidation;
    }
  }

  return true;
};

export const validateUniqueValue =
  (existingValues: string[], errorMessage: string) => (value: string) => {
    if (existingValues.includes(value.trim())) {
      return errorMessage;
    }

    return true;
  };

type ValidateURLParams = {
  errorMessage?: string;
  https?: boolean;
  privateProtocol?: boolean;
  isOptional?: boolean;
};
export const validateURL =
  ({
    errorMessage = "The provided URL is not in a valid format. Example valid url: https://example.com",
    https,
    privateProtocol,
    isOptional,
  }: ValidateURLParams) =>
  (url: string) => {
    try {
      if (url && !isOptional) {
        const urlData = new URL(url);

        if (https && urlData.protocol === "https:") {
          return true;
        }

        if (privateProtocol && urlData.protocol === "private:") {
          return true;
        }

        if (https || privateProtocol) {
          return errorMessage;
        }
      }

      return true;
    } catch {
      return errorMessage;
    }
  };

type ValidateRequiredURLParams = {
  requiredStringError?: string;
  urlError?: string;
  urlHttps?: boolean;
  urlPrivate?: boolean;
};
export const validateRequiredURL =
  ({ requiredStringError, urlError, urlHttps, urlPrivate }: ValidateRequiredURLParams = {}) =>
  (url: string) => {
    const requiredValidation = stringIsRequired(requiredStringError)(url);
    const urlValidation = validateURL({
      errorMessage: urlError,
      https: urlHttps,
      privateProtocol: urlPrivate,
    })(url);

    if (requiredValidation === true && urlValidation === true) {
      return true;
    }

    if (requiredValidation === true) {
      return urlValidation;
    }

    return requiredValidation;
  };

export const validateRegExp =
  (errorMessage: string = "The provided value is not a valid RegExp") =>
  (value: string) => {
    try {
      new RegExp(value);
      return true;
    } catch {
      return errorMessage;
    }
  };

export const validateDateIsAfterNow = (value: Date | undefined) => {
  if (!value) {
    return "Invalid date";
  }

  try {
    if (isBefore(value, new Date())) {
      return "You must select a future date";
    }

    return true;
  } catch {
    return "Invalid date";
  }
};

export const validateOptionalDateIsAfterNow = (value: Date | undefined) =>
  value ? validateDateIsAfterNow(value) : true;

// Fixed 24h days (not calendar days) so DST shifts match backend duration checks
export const getDateInDays = (days: number) => addHours(new Date(), days * 24);

export const validateDateIsWithinDays =
  (days: number, errorMessage: string = `Date can't be more than ${days} days from now`) =>
  (value: Date | undefined) =>
    !value || !isAfter(value, getDateInDays(days)) || errorMessage;

// Mirrors the bounds of the backend's standard cron parser; cronsTrue also accepts values past them, such as 7 for Sunday.
export const CRON_FIELD_BOUNDS: [number, number][] = [
  [0, 59], // minute
  [0, 23], // hour
  [1, 31], // day of month
  [1, 12], // month
  [0, 6], // day of week
];

export const CRON_TIMEZONE_PREFIX = /^(?:CRON_)?TZ=\S+\s+/;

const hasOutOfRangeCronValue = (value: string) =>
  value
    .trim()
    .replace(CRON_TIMEZONE_PREFIX, "")
    .split(/\s+/)
    .some((field, index) => {
      const bounds = CRON_FIELD_BOUNDS[index];
      if (!bounds) {
        return false;
      }

      const [lowest, highest] = bounds;
      return field
        .split(",")
        .flatMap((part) => {
          const [range] = part.split("/");
          // The backend reads a range starting with * or ? as the whole field and ignores the rest.
          return range.startsWith("*") || range.startsWith("?") ? [] : range.split("-");
        })
        .some(
          (fieldValue) =>
            /^\d+$/.test(fieldValue) &&
            (Number(fieldValue) < lowest || Number(fieldValue) > highest)
        );
    });

const HASHED_CRON_FIELD = /^H(?:\((\d+)-(\d+)\))?(?:\/(\d+))?$/;

const [minuteBounds, hourBounds, , monthBounds, dayOfWeekBounds] = CRON_FIELD_BOUNDS;

// Mirrors the backend: day of month is capped at 28 so the resolved day exists in every month.
const HASHED_CRON_FIELD_BOUNDS: [number, number][] = [
  minuteBounds,
  hourBounds,
  [1, 28],
  monthBounds,
  dayOfWeekBounds,
];

const isHashedCronField = (field: string) => field.startsWith("H");

const isValidHashedCronField = (field: string, [lowest, highest]: [number, number]) => {
  const match = field.match(HASHED_CRON_FIELD);
  if (!match) {
    return false;
  }

  const [, rangeFrom, rangeTo, step] = match;
  const from = rangeFrom === undefined ? lowest : Number(rangeFrom);
  const to = rangeTo === undefined ? highest : Number(rangeTo);

  if (from < lowest || to > highest || from > to) {
    return false;
  }

  return step === undefined || (Number(step) >= 2 && Number(step) <= to - from + 1);
};

// Swaps each H field for its lowest value so cronsTrue can check the rest; undefined when an H field is invalid.
const expandHashedCronFields = (value: string) => {
  const fields = value.trim().split(/\s+/);

  if (!fields.some(isHashedCronField)) {
    return value;
  }

  if (fields.length !== HASHED_CRON_FIELD_BOUNDS.length) {
    return undefined;
  }

  const expandedFields = fields.map((field, index) => {
    if (!isHashedCronField(field)) {
      return field;
    }

    const bounds = HASHED_CRON_FIELD_BOUNDS[index];
    return isValidHashedCronField(field, bounds) ? String(bounds[0]) : undefined;
  });

  return expandedFields.includes(undefined) ? undefined : expandedFields.join(" ");
};

const validateCronExpression =
  (errorMessage: string = "The provided value is not a valid cron expression") =>
  (value: string) => {
    try {
      const expandedValue = expandHashedCronFields(value.toString());

      if (!expandedValue || !cronsTrue.toString(expandedValue)) {
        return errorMessage;
      }

      return hasOutOfRangeCronValue(expandedValue) ? errorMessage : true;
    } catch {
      return errorMessage;
    }
  };

type ValidateRequiredCronExpression = {
  requiredStringError?: string;
  cronExpressionError?: string;
};
export const validateRequiredCronExpression =
  ({ requiredStringError, cronExpressionError }: ValidateRequiredCronExpression = {}) =>
  (cronExpression: string) => {
    const requiredValidation = stringIsRequired(requiredStringError)(cronExpression);
    const cronExpressionValidation = validateCronExpression(cronExpressionError)(cronExpression);

    if (requiredValidation === true && cronExpressionValidation === true) {
      return true;
    }

    if (requiredValidation === true) {
      return cronExpressionValidation;
    }

    return requiredValidation;
  };

const validateAccountName =
  (accountNameError: string = "The provided value is not a valid account name") =>
  (accountName: string) => {
    // https://github.com/spacelift-io/backend/blob/c478ca5fb43ad6aa19f7d706e0101e0f6b7e4263/database/account.go#L391
    const ACCOUNT_NAME_VALIDATION_PATTERN = /^[A-Za-z0-9][A-Za-z0-9\-_]{2,38}[A-Za-z0-9]$/;

    if (ACCOUNT_NAME_VALIDATION_PATTERN.test(accountName)) {
      return true;
    }

    return accountNameError;
  };

type ValidateRequiredAccountName = {
  requiredStringError?: string;
  accountNameError?: string;
};
export const validateRequiredAccountName =
  ({ requiredStringError, accountNameError }: ValidateRequiredAccountName) =>
  (accountName: string) => {
    const requiredValidation = stringIsRequired(requiredStringError)(accountName);
    const accountNameValidation = validateAccountName(accountNameError)(accountName);

    if (requiredValidation !== true) {
      return requiredValidation;
    }

    if (accountNameValidation !== true) {
      return accountNameValidation;
    }

    return true;
  };

export const validatePolicyName =
  (namesToValidate: string[], currentName?: string) => (value: string) => {
    if (currentName && currentName.toLowerCase() === value.trim().toLowerCase()) {
      return true;
    }

    const trimValue = value.trim();
    const requiredValidation = stringIsRequired("Name field is required.")(trimValue);
    if (requiredValidation !== true) {
      return requiredValidation;
    }

    const name = trimValue.toLowerCase();
    if (namesToValidate.includes(name)) {
      return "A policy with this name already exists in the account.";
    }
    return true;
  };

export const validateUniqueResourceName =
  (namesToValidate: string[], currentName?: string, errorMessage?: string) => (value: string) => {
    if (currentName && currentName === value) {
      return true;
    }

    const trimValue = value.trim();
    const requiredValidation = stringIsRequired("Name field is required.")(trimValue);
    if (requiredValidation !== true) {
      return requiredValidation;
    }

    const name = trimValue.toLowerCase();
    if (namesToValidate.includes(name)) {
      return errorMessage ?? "A resource with this name already exists in the account.";
    }
    return true;
  };

export const validateUrlField =
  (requiredErrorMessage: string = "URL is required") =>
  (value: string = "") => {
    const trimValue = value.trim();
    const requiredValue = stringIsRequired(requiredErrorMessage)(trimValue);
    if (requiredValue !== true) {
      return requiredValue;
    }

    const { err } = extractRepositoryDetails(trimValue);
    return err || undefined;
  };

const isValidHostnameSegment = (segment: string) =>
  segment.length > 0 &&
  segment.length <= 63 &&
  !segment.startsWith("-") &&
  !segment.endsWith("-") &&
  /^[a-z0-9-]+$/.test(segment);

// Mirrors the backend's rules https://github.com/spacelift-io/backend/blob/main/server/oauth2/redirect_allowlist.go
export const validateRedirectAllowlistHost = (value: string) => {
  const host = value.trim().toLowerCase();

  if (!host) {
    return true;
  }

  if (host.length > 253) {
    return "Host is too long (maximum is 253 characters).";
  }

  if (/[/?#@:\s]/.test(host)) {
    return "Enter a bare hostname - no scheme, port, or path.";
  }

  const base = host.startsWith("*.") ? host.slice(2) : host;

  if (host.startsWith("*.") && !base.includes(".")) {
    return `Wildcard "${host}" is too broad - cover a specific domain, e.g. *.example.com.`;
  }

  if (!base.split(".").every(isValidHostnameSegment)) {
    return "Enter a valid hostname, e.g. app.example.com or *.example.com.";
  }

  return true;
};
